import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Swiper from 'react-id-swiper';
import cx from 'classnames';
import get from 'lodash/get';
import styles from './CellGallery.scss';
import getCellGallerySwiperParams from './cellGalleryParams';
import thumbnailPic from '../../../../../../static/images/images_placeholder.svg';
import iconPdf from '../../../../../../static/icons/icon-pdf.png';
import modifyImageLink, { formatImageSrcSet } from '../../../../../utils/modifyImageLink';
import { imageModifierTypes } from '../../../../../constants';
import { getReadyImages } from '../../../../../utils/normalizeMediaStatus';

export default class CellGallery extends Component {
  state = {
    activeIndex: 0
  };

  componentDidMount() {
    window.addEventListener('orientationchange', this.resizeThrottler, false);
    requestAnimationFrame(this.resizeThrottler);
  }

  shouldComponentUpdate(newProps) {
    const isRtlChanged = newProps.isRtl !== this.props.isRtl;
    const isImagesChanged = newProps.images.length !== this.props.images.length
      || newProps.images?.some((image, idx) => get(image, 'status') !== get(this.props.images?.[idx], 'status'));

    return isRtlChanged || isImagesChanged || !!newProps.withoutLoop;
  }

  componentWillUnmount() {
    window.removeEventListener('orientationchange', this.resizeThrottler, false);
  }

  resizeThrottler = () => {
    if (this.swiperRef) {
      this.swiperRef.swiper.resize.resizeHandler();
    }
  };

  handleTransitionEnd = () => {
    const activeIndex = get(this.swiperRef, 'swiper.activeIndex');
    if (Number.isInteger(activeIndex)) {
      if (this.props.onSlideChange) {
        this.props.onSlideChange();
      }
      this.setState({ activeIndex });
    }
  };

  renderGalleryItem = (item, idx) => {
    const shouldLoadImage = (idx >= this.state.activeIndex - 1) && (idx <= this.state.activeIndex + 1);
    const handleClick = (evt) => evt.stopPropagation();

    if (item.raw) {
      return (
        <div
          className={styles.raw}
          key={item.thumbnail}
        >
          <img
            alt=""
            src={iconPdf}
          />
          <a href={item.thumbnail} onClick={handleClick}>{item.name}</a>
        </div>
      );
    }

    return (
      <div
        className={styles.image}
        key={item.thumbnail}
      >
        <img
          alt=""
          srcSet={formatImageSrcSet([
            {
              imageUrl: modifyImageLink(item.thumbnail, imageModifierTypes.GALLERY_THUMBNAIL) || thumbnailPic,
              width: 350
            },
            {
              imageUrl: modifyImageLink(item.thumbnail, imageModifierTypes.GALLERY_THUMBNAIL_MOBILE) || thumbnailPic,
              width: 600
            }
          ])}
          sizes="(max-width: 767px) 600px, (min-width: 768px) 350px"
          src={encodeURI(modifyImageLink(item.thumbnail, imageModifierTypes.GALLERY_THUMBNAIL)) || thumbnailPic}
        />
      </div>
    );
  };

  render() {
    const { isRtl, isLazy, withoutLoop, withoutPagination } = this.props;
    const images = getReadyImages(this.props.images);
    const gallerySwiperParams = getCellGallerySwiperParams({
      className: 'cellGalleryArrow',
      isMobile: true,
      isRtl,
      isLazy,
      withoutLoop
    });

    const params = {
      ...gallerySwiperParams,
      rtl: isRtl,
      noSwiping: true,
      initialSlide: this.state.activeIndex,
      loop: !withoutLoop,
      on: {
        slideChangeTransitionEnd: this.handleTransitionEnd
      },
      pagination: !withoutPagination && {
        el: '.swiper-pagination',
        clickable: true,
        dynamicBullets: true
      },
      preloadImages: false,
      lazy: { loadOnTransitionStart: true }
    };

    return (
      <div className={cx(styles.galleryWrapper, this.props.className)}>
        {(this.props.withoutRealEstateSlider || images.length <= 1) ? (
          this.renderGalleryItem(images[0] || {})
        ) : (
          <Swiper {...params} ref={(ref) => { this.swiperRef = ref; }}>
            { images.map((item, idx) => this.renderGalleryItem(item, idx)) }
          </Swiper>
        )}
      </div>
    );
  }
}

CellGallery.propTypes = {
  images: PropTypes.array,
  isRtl: PropTypes.bool,
  withoutLoop: PropTypes.bool,
  withoutPagination: PropTypes.bool,
  isLazy: PropTypes.bool,
  className: PropTypes.string,
  withoutRealEstateSlider: PropTypes.bool,
  onSlideChange: PropTypes.func
};
